import { useQueryClient } from '@tanstack/react-query';
import { useState, useCallback } from 'react';

import { CERTIFICATION_PROGRESS_QUERY_KEYS } from '@/hooks/queries/useCertificationProgress';
import { supabase } from '@/integrations/supabase/client';

/**
 * Hook for resetting certification progress
 * Completely isolated from the rest of the application
 * Designed for future admin-only access
 */
export const useResetCertification = () => {
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const resetCertification = useCallback(
    async (userId: string, caseId: string): Promise<boolean> => {
      setIsResetting(true);
      setError(null);

      try {
        // Delete all certification progress records for this user and case
        const { error: deleteError } = await supabase
          .from('certification_progress')
          .delete()
          .eq('user_id', userId)
          .eq('case_id', caseId);

        if (deleteError) {
          console.error('[ResetCertification] Error deleting records:', deleteError);
          setError(deleteError.message);
          return false;
        }

        console.log(
          '[ResetCertification] Successfully reset certification for user:',
          userId,
          'case:',
          caseId
        );

        // Invalidate all related queries
        void queryClient.invalidateQueries({
          queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byUserAndCase(userId, caseId),
        });
        void queryClient.invalidateQueries({
          queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.bestPassed(userId, caseId),
        });
        void queryClient.invalidateQueries({
          queryKey: CERTIFICATION_PROGRESS_QUERY_KEYS.byCase(caseId),
        });

        // Dispatch event to notify other components
        window.dispatchEvent(
          new CustomEvent('certification-updated', {
            detail: { userId, caseId, action: 'reset' },
          })
        );

        return true;
      } catch (err) {
        console.error('[ResetCertification] Unexpected error:', err);
        setError('An unexpected error occurred');
        return false;
      } finally {
        setIsResetting(false);
      }
    },
    [queryClient]
  );

  return {
    resetCertification,
    isResetting,
    error,
  };
};
